(() => {
  'use strict';

  class StickyATC {
    constructor(el) {
      this.el = el;
      this.sectionId = el.dataset.section;
      this.expanded = false;
      this.hasVariantSelected = false;

      this.bar = el.querySelector('.sticky-atc__bar');
      this.panel = el.querySelector('.sticky-atc__panel');
      this.expandBtn = el.querySelector('.sticky-atc__expand-btn');
      this.closeBtn = el.querySelector('.sticky-atc__close-btn');
      this.barAddBtn = el.querySelector('[data-sticky-bar-add]');
      this.formWrap = el.querySelector('.sticky-atc__submit-wrap');
      this.panelForm = el.querySelector(`#sticky-product-form-${this.sectionId}`);
      this.variantInput = el.querySelector(`#sticky-atc-variant-id-${this.sectionId}`);
      this.optionBtns = el.querySelectorAll('.sticky-atc__option-btn');
      this.panelSubmitBtn = el.querySelector('.sticky-atc__submit');

      const variantsEl = document.getElementById(`sticky-atc-variants-${this.sectionId}`);
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

      this.selectedOptions = {};
      this._wantsVisible = false;

      // Scroll thresholds (vh) per breakpoint — 0 means no threshold
      this._thresholdDesktop = parseFloat(el.dataset.scrollDesktop) || 0;
      this._thresholdTablet  = parseFloat(el.dataset.scrollTablet)  || 0;
      this._thresholdMobile  = parseFloat(el.dataset.scrollMobile)  || 0;

      // Move to end of body after third-party widgets load (e.g. #shopify-chat)
      document.body.appendChild(this.el);
      setTimeout(() => document.body.appendChild(this.el), 1500);

      this._bindEvents();
      this._observeMainATC();
      this._observeCartDrawer();
      this._lowerChatWidget();
      this._bindScrollThreshold();

      // Auto-apply for single default variant or when variant gating is disabled;
      // otherwise show the selection prompt
      const isSingleDefault = this.variants.length === 1 && this.variants[0].title === 'Default Title';
      const requireVariant = el.dataset.requireVariant !== 'false';
      if (isSingleDefault || !requireVariant) {
        const firstAvailable = this.variants.find(v => v.available) || this.variants[0];
        if (firstAvailable) this._applyVariant(firstAvailable);
      } else {
        this._showSelectPrompt();
      }
    }

    _bindEvents() {
      if (this.expandBtn) {
        this.expandBtn.addEventListener('click', () => this.togglePanel());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.collapsePanel());
      }

      this.optionBtns.forEach(btn => {
        btn.addEventListener('click', () => this._selectOption(btn));
      });

      if (this.barAddBtn) {
        this.barAddBtn.addEventListener('click', () => {
          if (this.expandBtn) {
            this.expandPanel();
          } else {
            this._submitCart();
          }
        });
      }

      if (this.panelForm) {
        this.panelForm.addEventListener('submit', e => {
          e.preventDefault();
          this._submitCart();
        });
      }

      document.addEventListener('click', e => {
        if (this.expanded && !this.el.contains(e.target)) {
          this.collapsePanel();
        }
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.expanded) this.collapsePanel();
      });

      // Sync when main product variant picker changes (user explicitly picks on main form)
      const mainVariantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
      if (mainVariantSelects) {
        mainVariantSelects.addEventListener('change', () => {
          requestAnimationFrame(() => this._syncFromMainForm());
        });
      }
    }

    _observeCartDrawer() {
      new MutationObserver(() => {
        if (document.body.classList.contains('overflow-hidden')) {
          this._hide();
        } else {
          this._show();
        }
      }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    _lowerChatWidget() {
      const apply = () => {
        const chat = document.getElementById('shopify-chat');
        if (chat) chat.style.zIndex = '9998';
      };
      apply();
      new MutationObserver(apply).observe(document.body, { childList: true, subtree: false });
    }

    _showSelectPrompt() {
      if (!this.formWrap || this.formWrap.querySelector('.sticky-atc__select-prompt')) return;
      const prompt = document.createElement('div');
      prompt.className = 'sticky-atc__select-prompt';
      prompt.textContent = 'Select options';
      this.formWrap.appendChild(prompt);
    }

    _hideSelectPrompt() {
      this.el.querySelectorAll('.sticky-atc__select-prompt').forEach(p => p.remove());
    }

    _observeMainATC() {
      const mainBtn = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!mainBtn) return;

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._hide();
          } else {
            this._show();
          }
        });
      }, { threshold: 0 });

      observer.observe(mainBtn);
    }

    _syncFromMainForm() {
      const mainInput = document.querySelector(`#product-form-${this.sectionId} .product-variant-id`);
      if (!mainInput || !mainInput.value) return;
      const variantId = parseInt(mainInput.value);
      if (!variantId) return;
      const variant = this.variants.find(v => v.id === variantId);
      if (variant) this._applyVariant(variant);
    }

    _getScrollThresholdPx() {
      const w = window.innerWidth;
      const vh = w < 750 ? this._thresholdMobile
               : w < 990 ? this._thresholdTablet
               : this._thresholdDesktop;
      return (vh / 100) * window.innerHeight;
    }

    _bindScrollThreshold() {
      window.addEventListener('scroll', () => {
        if (!this._wantsVisible) return;
        if (window.scrollY >= this._getScrollThresholdPx()) {
          this.el.classList.add('is-visible');
          this.el.setAttribute('aria-hidden', 'false');
        } else {
          this.el.classList.remove('is-visible');
          this.el.setAttribute('aria-hidden', 'true');
        }
      }, { passive: true });
    }

    _show() {
      this._wantsVisible = true;
      if (window.scrollY >= this._getScrollThresholdPx()) {
        this.el.classList.add('is-visible');
        this.el.setAttribute('aria-hidden', 'false');
      }
    }

    _hide() {
      this._wantsVisible = false;
      this.el.classList.remove('is-visible');
      this.el.setAttribute('aria-hidden', 'true');
      this.collapsePanel();
    }

    togglePanel() {
      this.expanded ? this.collapsePanel() : this.expandPanel();
    }

    expandPanel() {
      this.expanded = true;
      this.el.classList.add('is-expanded');
      this.panel.removeAttribute('hidden');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'true');
    }

    collapsePanel() {
      this.expanded = false;
      this.el.classList.remove('is-expanded');
      this.panel.setAttribute('hidden', '');
      if (this.expandBtn) this.expandBtn.setAttribute('aria-expanded', 'false');
    }

    _selectOption(btn) {
      if (btn.hasAttribute('data-unavailable')) return;

      const index = parseInt(btn.dataset.optionIndex);
      this.selectedOptions[index] = btn.dataset.value;

      // Update button states for this option group
      this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`).forEach(b => {
        b.classList.toggle('is-selected', b === btn);
      });

      // Update displayed value
      const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
      if (display) display.textContent = btn.dataset.value;

      // Find and apply the matching variant
      const variant = this._findVariant();
      this._applyVariant(variant);

      // Also update the main product form so everything stays in sync
      this._syncToMainForm(variant);
    }

    _findVariant() {
      return this.variants.find(v =>
        v.options.every((opt, i) => this.selectedOptions[i] === opt)
      ) || null;
    }

    _applyVariant(variant) {
      // Update selected options display to match variant
      if (variant) {
        variant.options.forEach((value, index) => {
          this.selectedOptions[index] = value;
          this.el.querySelectorAll(`.sticky-atc__option-btn[data-option-index="${index}"]`).forEach(btn => {
            btn.classList.toggle('is-selected', btn.dataset.value === value);
          });
          const display = this.el.querySelector(`.sticky-atc__option-value[data-option-index="${index}"]`);
          if (display) display.textContent = value;
        });
      }

      const available = variant && variant.available;

      if (this.variantInput && variant) {
        this.variantInput.value = variant.id;
      }

      // Show or hide the selection prompt
      if (variant) {
        this.hasVariantSelected = true;
        this._hideSelectPrompt();
      } else {
        this.hasVariantSelected = false;
        this._showSelectPrompt();
      }

      // Update panel submit button
      if (this.panelSubmitBtn) {
        this.panelSubmitBtn.disabled = !available;
        const span = this.panelSubmitBtn.querySelector('span') || this.panelSubmitBtn;
        span.textContent = !variant ? 'Select options' : (available ? 'Add to Cart' : 'Sold Out');
      }

      // Update bar add button
      if (this.barAddBtn) {
        this.barAddBtn.disabled = !available;
        const span = this.barAddBtn.querySelector('span') || this.barAddBtn;
        span.textContent = !variant ? 'Select options' : (available ? 'Add to Cart' : 'Sold Out');
      }
    }

    _syncToMainForm(variant) {
      if (!variant) return;

      // Update the hidden variant ID input only — do not dispatch change events on the
      // variant selects, as that triggers the theme's async fetch which updates the URL
      // and causes the page to scroll to the top.
      const mainInput = document.querySelector(`#product-form-${this.sectionId} .product-variant-id`);
      if (mainInput) mainInput.value = variant.id;
    }

    _getOptionName(index) {
      const btn = this.el.querySelector(`.sticky-atc__option-btn[data-option-index="${index}"]`);
      if (!btn) return '';
      const group = btn.closest('.sticky-atc__option-group');
      const legend = group ? group.querySelector('legend') : null;
      return legend ? legend.firstChild.textContent.trim().replace(/:$/, '') : '';
    }

    _submitCart() {
      const variantId = this.variantInput ? parseInt(this.variantInput.value) : null;
      if (!variantId) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const btns = [this.panelSubmitBtn, this.barAddBtn].filter(Boolean);
      btns.forEach(btn => {
        btn.disabled = true;
        const span = btn.querySelector('span') || btn;
        span.textContent = 'Adding…';
      });

      const body = { id: variantId, quantity: 1 };
      if (cart) {
        body.sections = cart.getSectionsToRender().map(s => s.id).join(',');
        body.sections_url = window.location.pathname;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(res => {
          if (!res.ok) throw new Error();
          return res.json();
        })
        .then(parsedState => {
          btns.forEach(btn => {
            const span = btn.querySelector('span') || btn;
            span.textContent = 'Added!';
          });

          if (cart && parsedState.sections) {
            cart.renderContents(parsedState);
          }

          this._hide();

          setTimeout(() => {
            const variant = this.variants.find(v => v.id === variantId);
            this._applyVariant(variant);
          }, 1800);
        })
        .catch(() => {
          btns.forEach(btn => {
            btn.disabled = false;
            const span = btn.querySelector('span') || btn;
            span.textContent = 'Add to Cart';
          });
        });
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.sticky-atc').forEach(el => new StickyATC(el));
  });
})();
